import { SWRApiError } from "@/hooks/swr";
import { MeasurementRequestMinimal } from "@/types/measurementRequest";
import { Calendar, Plus, User } from "lucide-react";
import { Link } from "react-router";
import { Badge } from "../../ui/badge";
import {
  getMeasurementRequestStatusColor,
  measurementRequestStatusMapping,
} from "../../shared/utils";
import { Button } from "../../ui/button";
import { StepCardWrapper } from "./StepCardWrapper";

type MeasurementRequestStepProps = {
  error?: SWRApiError;
  isLoading: boolean;
  isLocked: boolean;
  measurementRequests: MeasurementRequestMinimal[];
  openMeasurementRequestDialog: (open: boolean) => void;
};

export const MeasurementRequestStep = ({
  error,
  isLoading,
  isLocked,
  measurementRequests,
  openMeasurementRequestDialog,
}: MeasurementRequestStepProps) => {
  if (isLoading) {
    return (
      <StepCardWrapper title="Measurement Requests">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
          Loading measurement requests...
        </div>
      </StepCardWrapper>
    );
  }

  if (error) {
    return (
      <StepCardWrapper title="Measurement Requests">
        <div className="text-sm text-red-600">Error loading measurement requests</div>
      </StepCardWrapper>
    );
  }

  if (measurementRequests.length === 0) {
    return (
      <StepCardWrapper title="Measurement Requests">
        <div className="space-y-2">
          <p className="text-sm text-gray-500">No measurement requests found for this mask</p>
          <Button
            disabled={isLocked}
            className="w-full"
            type="button"
            onClick={() => openMeasurementRequestDialog(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Measurement Request
          </Button>
        </div>
      </StepCardWrapper>
    );
  }

  return (
    <StepCardWrapper title="Measurement Requests">
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">Linked Measurement Requests:</p>
        {measurementRequests.map((request) => (
          <div key={request.id} className={`p-3 rounded-md border`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <Link
                  to={`/measurement-requests/${request.id}`}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                >
                  {request.name}
                </Link>
                <div className="flex items-center gap-3 mt-1">
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-500">{request.createdBy}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-500">
                      {request.createdAt.toLocaleDateString("en-US", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                </div>
              </div>
              <Badge
                className={getMeasurementRequestStatusColor(request.status)}
                variant="secondary"
              >
                {measurementRequestStatusMapping[request.status]}
              </Badge>
            </div>
          </div>
        ))}
        <Button
          className="w-full"
          disabled={isLocked}
          type="button"
          onClick={() => openMeasurementRequestDialog(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Measurement Request
        </Button>
      </div>
    </StepCardWrapper>
  );
};
